import React, {
  useState,
  useEffect,
  FormEvent,
  FocusEvent,
  KeyboardEvent,
} from "react";
import "../styles/ResetPassword.css";
import axios from "axios";
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate, Navigate } from "react-router-dom";
import openNotification from "./utils/CustomNotification";
import styled from "styled-components";
import logo from "./assets/logo.png";

const SvgWrapper = styled.div`
  width: 100%;
  height: 350px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center; // This vertically centers content
  flex-direction: column; // Stack icon and text
  z-index: 3;

  @media (max-width: 768px) {
    height: 280px; /* Adjust for mobile */
  }

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%; /* Make SVG fill the wrapper */
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  color: #666; /* Adjust color as needed */
  font-size: 1rem; /* Adjust font size as needed */

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #ccc; /* Adjust line color as needed */
    margin: 0 1rem;
  }
`;

const Card = styled.div`
  /* width: 400px; */
  width: 30%;
  background-color: #ffffff;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-height: 98vh; /* Adjust this value as needed */
  overflow-y: auto;

  /* Hide scrollbar for Webkit-based browsers */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for Firefox */
  scrollbar-width: none;

  @media (max-width: 1024px) {
    width: 40%;
  }

  @media (max-width: 768px) {
    width: 50%;
  }

  @media (max-width: 480px) {
    width: 90%;
  }
`;
const Down = styled.div`
  /* display: flex; */
  /* background-color: #00d2ff; */
  padding: 2rem;
`;
const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 1rem; /* 25px equivalent */
  line-height: 1.2rem; /* 30.26px equivalent */
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  @media (min-width: 1500px) {
    margin-top: 13%;
  }
  @media (min-width: 2000px) {
    margin-top: 20%;
  }

  span {
    display: block; /* Ensures each part is on a separate line */
  }
`;

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [otpInput, setOtpInput] = useState<string[]>(new Array(6).fill(""));
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpSubmitting, setOtpSubmitting] = useState<boolean>(false);
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [otpSubmitted, setOtpSubmitted] = useState<boolean>(false);
  const [updatedSessionUid, setUpdatedSessionUid] = useState<string | null>(
    null
  );
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [passwordValidationMessages, setPasswordValidationMessages] = useState<
    string[]
  >([]);
  const [otpAttempts, setOtpAttempts] = useState<number>(0);
  const [timer, setTimer] = useState<number>(60);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const navigate = useNavigate();

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timer]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value;
    setEmail(emailValue);
    setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue));
    localStorage.setItem("user_email", emailValue);
  };

  const handleGenerateOtp = async (event: FormEvent) => {
    event.preventDefault();
    if (!email) {
      openNotification({
        type: "error",
        message: "Please enter your email",
      });
      return;
    }

    try {
      const validationResponse = await axios.post(
        `${beAddress}/api/password-expiry-validate-user`,
        {},
        {
          headers: {
            "user-uid": email,
          },
          withCredentials: true,
        }
      );
      //const validationResponse = { data: { data: { session_uid: "12345" }, message: "User validated successfully" } };

      const sessionUidFromResponse = validationResponse.data.data.session_uid;
      const emailFromResponse = validationResponse.data.data.email;
      if (emailFromResponse) {
        //       localStorage.setItem("session_uid", sessionUidFromResponse);

        // if (emailFromResponse) {
        localStorage.setItem("email", emailFromResponse);

        setOtpSent(true);
        setIsTimerActive(true);
        setTimer(60);
        openNotification({
          type: "success",
          message: "OTP has been sent to your email",
        });
      } else {
        openNotification({
          type: "error",
          message: validationResponse.data.message,
        });
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Error occurred while generating OTP",
      });
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (/^[0-9]$/.test(value) || value === "") {
      const newOtpInput = [...otpInput];
      newOtpInput[index] = value;
      setOtpInput(newOtpInput);
      if (value && index < 5) {
        document.getElementById(`reset-otp-input-${index + 1}`)?.focus();
      } else if (value === "" && index > 0) {
        document.getElementById(`reset-otp-input-${index}`)?.focus();
      }
    }
  };

  const resetForm = () => {
    setEmail("");
    setEmailValid(false);
    setOtpInput(new Array(6).fill(""));
    setOtpSent(false);
    setOtpAttempts(0);
    setOtpSubmitted(false);
  };

  const handleOTPSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (otpSubmitting) return;
    setOtpSubmitting(true);

    const otp = otpInput.join("").trim();
    if (!otp) {
      openNotification({
        type: "error",
        message: "Please enter OTP",
      });
      setOtpSubmitting(false);
      return;
    }

    try {
     
      const email = localStorage.getItem("user_email");

      if (!email) {
        openNotification({
          type: "error",
          message: "Session expired. Please try again.",
        });
        setOtpSubmitting(false);
        return;
      }

      const response = await axios.post(
        `${beAddress}/api/password-expiry-validate-user-otp`,
        otp,
        {
          headers: {
            "Content-Type": "text/plain",
            "user-uid": email,
          },
          withCredentials: true,
        }
      );
      // const response = {
      //   status: 200,
      //   data: {
      //     status: "Success",
      //     message: "OTP validated successfully",
      //     data: { session_uid: "67890" },
      //   },
      // };

      if (response.status === 200 && response.data.status === "Success") {
        setUpdatedSessionUid(response.data.data.session_uid);
        openNotification({
          type: "success",
          message: response.data.message,
        });
        setOtpSubmitted(true);
        setOtpAttempts(0);
      } else {
        setOtpAttempts((prev) => prev + 1);
        openNotification({
          type: "error",
          message: response.data.data,
        });
        if (otpAttempts + 1 >= 3) {
          resetForm();
        }
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Error validating OTP",
      });
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handlePasswordReset = async (event: FormEvent) => {
    event.preventDefault();

    if (newPassword !== confirmNewPassword) {
      openNotification({
        type: "error",
        message: "New passwords do not match",
      });
      return;
    }

    if (!oldPassword) {
      openNotification({
        type: "error",
        message: "Please enter your old password",
      });
      return;
    }

    try {
      const response = await axios.post(
        `${beAddress}/api/change-expired-password`,
        {
          user_uid: email,
          current_password: oldPassword,
          new_password: newPassword,
          confirm_password: confirmNewPassword,
          otp: otpInput.join(""),
        },
        {
          headers: {
            "user-uid": email,
          },
          withCredentials: true,
        }
      );

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.message,
        });
        navigate("/");
      } else {
        openNotification({
          type: "error",
          message: response.data.data,
        });
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Error updating password",
      });
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const pasteDigits = pasteData.match(/\d/g);

    if (pasteDigits) {
      const newOtpInput = [
        ...new Array(6).fill("").map((_, i) => pasteDigits[i] || ""),
      ];

      setOtpInput(newOtpInput);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleOTPSubmit(e as unknown as FormEvent);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    const messages = validatePassword(password);
    setPasswordValidationMessages(messages);
    setShowTooltip(messages.length > 0);
  };

  const handleFocus = () => {
    if (passwordValidationMessages.length > 0) {
      setShowTooltip(true);
    }
  };

  const handleBlur = () => {
    setShowTooltip(false);
  };

  const Tooltip: React.FC<{ visible: boolean; children: React.ReactNode }> = ({
    children,
    visible,
  }) => {
    return (
      <div className={`reset-tooltip ${visible ? "visible" : ""}`}>
        {children}
      </div>
    );
  };

  const validatePassword = (password: string): string[] => {
    const messages: string[] = [];
    if (password.length < 8) {
      messages.push("Password must be at least 8 characters long.");
    }
    if (!/^[A-Z]/.test(password)) {
      messages.push("Password must include at least one uppercase letter.");
    }
    if (!/[a-z]/.test(password)) {
      messages.push("Password must include at least one lowercase letter.");
    }
    if (!/\d/.test(password)) {
      messages.push("Password must include at least one number.");
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      messages.push("Password must include at least one special character.");
    }
    return messages;
  };

  const maskEmail = (email: string): string => {
    const [username, domain] = email.split("@");
    const maskedUsername = username.slice(0, 4) + "****"; // Mask all but first four characters
    return `${maskedUsername}@${domain}`;
  };

  return (
    <div className="reset-auth-container">
      {/* <div className="reset-auth-right"> */}

      <Card>
        <div style={{ position: "relative", width: "100%", height: "150px" }}>
          {/* Background SVG */}
          {/* Background SVG */}
          <SvgWrapper>
            <svg
              viewBox="0 0 566 261"
              preserveAspectRatio="xMinYMin meet"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                position: "absolute",
                top: "10px",
                left: "0",
                width: "100%",
                height: "auto", // Allow the height to scale with width
                zIndex: 1,
              }}
            >
              <g filter="url(#filter0_d_491_18403)">
                <path
                  d="M0 0H566V121.87C374.085 86.504 334.083 349.97 0 106.787V0Z"
                  fill="#084852"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_491_18403"
                  x="-4"
                  y="0"
                  width="574"
                  height="269"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_491_18403"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_491_18403"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>

            {/* Foreground SVG */}
            <svg
              viewBox="0 0 566 221"
              preserveAspectRatio="xMinYMin meet"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "auto", // Allow the height to scale with width
                zIndex: 2,
              }}
            >
              <defs>
                <clipPath id="clipRoundedCorners">
                  <rect x="0" y="0" width="566" height="221" rx="20" ry="20" />
                </clipPath>
                <filter
                  id="filter0_d_491_18404"
                  x="-4"
                  y="0"
                  width="574"
                  height="221"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_491_18404"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_491_18404"
                    result="shape"
                  />
                </filter>
              </defs>
              <g
                clipPath="url(#clipRoundedCorners)"
                filter="url(#filter0_d_491_18404)"
              >
                <path
                  d="M0 0H566V107.104C373.56 101.356 326.5 359.172 0 90.4174V0Z"
                  fill="#AAEFF4"
                />
              </g>
            </svg>
          </SvgWrapper>

          {/* Centered Image and Text */}
          <div
            style={{
              position: "absolute",
              top: "35%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3,
              textAlign: "center",
            }}
          >
            <img
              src={logo}
              alt="Center Image"
              style={{ width: "60px", height: "50px", marginBottom: "10px" }}
            />
            <div
              style={{
                fontSize: "clamp(1.2rem, 2vw, 2rem)", // Consistent size definition
                color: "#000",
                fontWeight: "bold",
                fontFamily: "Inter", // Correct camelCase
                lineHeight: "36.31px",
                letterSpacing: "-0.24px", // Simplified for readability
                textAlign: "center",
                textUnderlinePosition: "from-font",
                textDecorationSkipInk: "none",
              }}
            >
              cofit
            </div>
          </div>
        </div>

        <Down>
          <Subtitle>
            <span>Corporate Wellness</span>
            <span>Redefined.</span>
          </Subtitle>
          <h1 className="forgot-auth-title">Reset Password</h1>

          <form
            className="reset-auth-form"
            onSubmit={otpSent ? handleOTPSubmit : handleGenerateOtp}
          >
            {!otpSent && (
              <>
                <div className="reset-auth-input-group">
                  <label htmlFor="email" className="reset-auth-label">
                    User ID
                  </label>
                  <input
                    type="email"
                    id="email"
                    className={`auth-input ${emailValid ? "valid" : "invalid"}`}
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  {emailValid ? (
                    <FaCheck className="reset-valid-icon" />
                  ) : (
                    <FaTimes className="reset-invalid-icon" />
                  )}
                </div>
                <button type="submit" className="reset-auth-button">
                  Generate OTP
                </button>
              </>
            )}

            {otpSent && !otpSubmitted && (
              <>
                <div className="reset-otp-inputs">
                  <p className="reset-auth-otp-instructions">
                    Please enter the OTP sent to your {email}
                  </p>
                  <div className="reset-otp-boxes">
                    {otpInput.map((otp, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength={1}
                        className="reset-otp-input"
                        value={otp}
                        onChange={(e) => handleOtpChange(e.target.value, index)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Backspace" &&
                            index > 0 &&
                            otp === ""
                          ) {
                            document
                              .getElementById(`reset-otp-input-${index - 1}`)
                              ?.focus();
                          } else if (e.key === "Enter") {
                            handleKeyDown(e);
                          }
                        }}
                        onPaste={handlePaste}
                        id={`reset-otp-input-${index}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleOTPSubmit}
                    className="reset-auth-button"
                    disabled={otpSubmitting}
                  >
                    Submit OTP
                  </button>

                  <div className="forgot-otp-inputs">
                    <div className="otp-timer">
                      {isTimerActive ? (
                        <p>Resend OTP in {timer} seconds</p>
                      ) : (
                        <p>
                          <span
                            className="resend-otp-link"
                            onClick={handleGenerateOtp}
                            style={{
                              cursor: timer === 0 ? "pointer" : "not-allowed",
                              color: timer === 0 ? "blue" : "gray",
                            }}
                          >
                            Resend OTP
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {otpSubmitted && (
              <>
                <div className="reset-auth-input-group">
                  <label htmlFor="oldPassword" className="reset-auth-label">
                    Old Password
                  </label>
                  <input
                    type={showOldPassword ? "text" : "password"}
                    id="oldPassword"
                    className="auth-input"
                    placeholder="Enter your old password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                  />
                  <span
                    className="reset-password-icon"
                    onClick={() => {
                      setShowOldPassword((prevState) => {
                        //console.log("Previous state:", prevState); // log the previous state
                        return !prevState;
                      });
                    }}
                  >
                    {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <div className="reset-auth-input-group">
                  <label htmlFor="newPassword" className="reset-auth-label">
                    New Password
                  </label>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    className="auth-input"
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  <span
                    className="reset-password-icon"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                  <Tooltip visible={showTooltip}>
                    <ul>
                      {passwordValidationMessages.map((message, index) => (
                        <li key={index}>{message}</li>
                      ))}
                    </ul>
                  </Tooltip>
                </div>

                <div className="reset-auth-input-group">
                  <label
                    htmlFor="confirmNewPassword"
                    className="reset-auth-label"
                  >
                    Confirm New Password
                  </label>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmNewPassword"
                    className="auth-input"
                    placeholder="Confirm your new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                  <span
                    className="reset-password-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <button
                  onClick={handlePasswordReset}
                  className="reset-auth-button"
                  disabled={
                    !oldPassword ||
                    !newPassword ||
                    !confirmNewPassword ||
                    newPassword !== confirmNewPassword ||
                    passwordValidationMessages.length > 0
                  }
                >
                  Reset Password
                </button>
              </>
            )}
          </form>
        </Down>
      </Card>

      {/* </div> */}
    </div>
  );
};

export default ResetPassword;
